import React, { useState, useEffect, useRef } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import { Grid, Container, Row, Column } from "ui-grid";
import {
  Button,
  Tooltip,
  Icon,
  ErrorMessage,
  typography,
  theme,
  ErrorWarningBottomSheet,
} from "ui-components";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { DatePickerDropdownNewFixed } from "../../components/new/dropdown/new-date-picker-dropdown-fixed";
import Big from "big.js";
import { startOfMonth, add, setDate, startOfDay } from "date-fns";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { autoPayDetailsSchema } from "./utils/validation-schemas-payment";
import { PaymentSourceType, AutopayFields } from "../../enums";
import { translate } from "../../i18n";
import {
  getPropertyFees,
  setDefaultPaymentSourceUpdated,
  setPaymentStarted,
} from "../../redux/actions/actionCreators";
import { useDispatch, useSelector } from "react-redux";
import { usePaymentMethodInformation } from "../../hooks/usePaymentMethodInformation";
import { RootState } from "../../redux/reducers";
import { DomusoApiService } from "../../api/DomusoApiService";
import { isAvsReady } from "../../utils/vantiv";
import PaymentMethods from "../../components/new/payment-flow/payment-methods";
import { paymentMethodsAutopayFlow } from "./utils/business-logic-payment-methods";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { PaymentAutopayFlowProgressIndicator } from "../../components/new/payment-autopay-flow/payment-autopay-flow-progress-indicator";
import { VerifyBillingAddressNew } from "../../components/new/verify-billing-address/verify-billing-address";
import moment from "moment";
import Auryc from "rn-auryc-sdk";

const { CREDIT_CARD, BANK_ACCOUNT } = PaymentSourceType;
interface ErrorMsgObjectProps {
  title?: string;
  body?: string;
}

export const PaymentAutoPayDetails = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const styles = getStyles();
  const logger = useSelector((state: RootState) => state.logger);
  const { params } = route;
  const {
    propertyUnit,
    balanceDue = 0,
    reservationId,
    currentPropertyProfile,
    currentPropertyFeeProfile,
    allowedPayments,
  } = params;
  const { autopay_start_day: autoPayStartDay = 1, autopay_end_day: autoPayEndDay = 5 } =
    currentPropertyProfile;
  let {
    certified_start_day,
    certified_end_day,
    allow_ach_payments,
    allow_card_payments,
    allow_domuso_cash = false,
  } = currentPropertyProfile || {};
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const preSelectedPaymentSourceId = useSelector((state) => state.payment.paymentSourceId);
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const {
    paymentSources,
    firstValidSource,
    error: paymentSourcesError,
  } = usePaymentMethodInformation({
    certifiedDateRange: { certified_end_day, certified_start_day },
    allowedPayments,
    preSelectedPaymentSourceId,
    keepInactive: false,
    allowACH: !!allow_ach_payments,
    allowCC: !!allow_card_payments,
  });

  const [amount, setAmount] = React.useState(Big(isNaN(balanceDue) ? 0 : balanceDue || 0));
  const [hasUserCCPayBlock, setHasUserCCPayBlock] = useState(false);
  const [selected, emitSelected] = React.useState<any>();
  const [preSelected, emitPreSelected] = useState<any>();
  const [isLoading, setLoading] = useState(false);
  let [toggleForCardBottomSheet, setToggleForCardBottomSheet] = useState(false);
  let [finalAmountsState, setFinalAmountsState] = useState();
  const [errorMsgObject, setErrorMsgObject] = useState<ErrorMsgObjectProps>({});

  const [ccFeesMessage, setCcFeesMessage] = useState("");
  const scrollViewRef = useRef<ScrollView>(null);
  const verifyBillAddressPopupRef = useRef<any>();
  const errorPopupRef = useRef<any>();

  const isCertifiedPaymentAllowed = () => {
    if (!allow_domuso_cash) {
      return false;
    }
    return true;
  };

  const scrollToTop = () => {
    if (Platform.OS === "web") {
      window?.scroll({ top: 0 });
    } else {
      scrollViewRef?.current?.scrollTo({ y: 0 });
    }
  };
  const setDefaultPaymentSource = () => {
    let paymentSource = firstValidSource;
    if (paymentSources && paymentSources.length && preSelectedPaymentSourceId) {
      paymentSource = paymentSources.find((source) => source.id === preSelectedPaymentSourceId);
    }
    if (paymentSource) {
      scrollToTop();
      emitSelected({ ...paymentSource, paymentType: paymentSource.type });
      emitPreSelected({ ...paymentSource, paymentType: paymentSource.type });
    }
  };

  useEffect(() => {
    if (preSelectedPaymentSourceId) {
      setDefaultPaymentSource();
    }
  }, [firstValidSource]);

  const getDifferenceInTimeZone = () => {
    var now = moment();
    var localOffset = now.utcOffset();
    now.tz("America/Los_Angeles");
    var centralOffset = now.utcOffset();
    var diffInMinutes = localOffset - centralOffset;
    return diffInMinutes;
  };

  const dispatch = useDispatch();
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);
  const startOfNextMonth = startOfMonth(add(new Date(), { months: 1 }));
  const minimumDate = setDate(startOfNextMonth, autoPayStartDay);
  const maximumDate = setDate(startOfNextMonth, autoPayEndDay);
  const [autopayDate, setAutopayDate] = useState(undefined);
  const [autopayDateDisplay, setAutopayDateDisplay] = useState("");

  let defaultValues: any = {
    paymentAmount: amount,
    paymentDate: autopayDate,
  };
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(autoPayDetailsSchema({ minimumDate, maximumDate })),
    defaultValues,
  });

  const layoutEffect = React.useCallback(() => {
    dispatch(setPaymentStarted(reservationId));
    domusoApiService
      .checkUserCCPayBlock({ reservationId })
      .then((blockedRes) => {
        setHasUserCCPayBlock(!!blockedRes.blocked);
      })
      .catch((e) => {
        logger.error(e);
      });
  }, []);
  useFocusEffect(layoutEffect);

  const showUnexpectedError = (error?: any) => {
    setLoading(false);
    if (
      error &&
      (error == "networkError" ||
        error?.message == "Failed to fetch" ||
        error?.message == "Network request failed")
    ) {
      setErrorMsgObject({
        title: translate("autopay.details.connectionErrorTitle") as string,
        body: translate("autopay.details.connectionErrorMessage") as string,
      });
      errorPopupRef?.current?.open();
      return;
    } else {
      setErrorMsgObject({
        title: translate("autopay.details.error") as string,
        body: translate("autopay.details.unexpectedError") as string,
      });
      errorPopupRef?.current?.open();
      return;
    }
  };

  useEffect(() => {
    if (paymentSourcesError) {
      showUnexpectedError(paymentSourcesError);
    }
  }, [paymentSourcesError]);

  useEffect(() => {
    register(AutopayFields.PAYMENT_DATE);
    register(AutopayFields.PAYMENT_AMOUNT);
  }, [register, paymentSources]);

  useEffect(() => {
    dispatch(setDefaultPaymentSourceUpdated(null));
  }, []);

  const updateSelected = (value: any) => {
    // setIsDuplicatePayment(false);
    emitSelected(value);
    scrollToTop();

    if (value.card_type) {
      if (value?.currentPropertyFeeProfile?.rent_certified_cc_percent) {
        const perBlock = 1000;
        const ccFeeToChager =
          (value.currentPropertyFeeProfile.rent_certified_cc_percent / 100) * perBlock;
        setCcFeesMessage(
          `*for $${perBlock.toLocaleString()}, the fee would be $${ccFeeToChager.toLocaleString()}`
        );
      }
    } else {
      setCcFeesMessage("");
    }
  };

  const getDisabled = () => {
    // if()
    // Could be more conditions in the future
    return (
      isLoading || !selected || (selected?.type == CREDIT_CARD && !isCertifiedPaymentAllowed())
    );
  };

  const onSubmit = async (data: any) => {
    try {
      const { paymentDate, paymentAmount = 0 } = data;
      const amounts = {
        amount: Big(paymentAmount),
        // These are determined later
        platformFee: Big(0),
        total: Big(paymentAmount),
      };

      if (selected.id === PaymentSourceType.MONEYGRAM) {
        //todo moises this MoneygramInformationPage was deleted if used need to point to /new/
        navigation.navigate("MoneygramInformationPage", {
          ...params,
          propertyUnit,
          reservationId,
          amounts,
          paymentSourceId: selected,
        });
        return;
      }

      const finalAmounts: any = {
        amount: Big(amounts.amount || 0),
      };
      let { last_four_digits, type, id, card_type, bank_account_type } = selected;

      if (type === PaymentSourceType.BANK_ACCOUNT) {
        finalAmounts.platformFee = Big(propertyFees?.bankAccount?.fee);
        finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
        card_type = bank_account_type;
      } else if (type === PaymentSourceType.CREDIT_CARD) {
        finalAmounts.platformFee = propertyFees?.ccDebitCard?.fee;
        finalAmounts.total = finalAmounts.amount.add(finalAmounts.platformFee);
      }
      setFinalAmountsState(finalAmounts);

      if (type === PaymentSourceType.CREDIT_CARD && !isAvsReady(selected)) {
        verifyBillAddressPopupRef?.current?.open();
        setToggleForCardBottomSheet(!toggleForCardBottomSheet);
        return;
      }

      return navigation.navigate("PaymentAutoPayReviewPayment", {
        ...params,
        balanceDue,
        propertyUnit,
        reservationId,
        paymentSourceId: id,
        paymentScheduleType: "RECURRING",
        paymentDate,
        paymentDateDisplay: autopayDateDisplay,
        amounts: finalAmounts,
        last4Digits: last_four_digits,
        paymentSourceType: type,
        cardType: card_type,
        fromAutopay: true,
        nickname: selected.nickname,
        selectedPaymentMethod: selected,
      });
    } catch (error) {
      showUnexpectedError(error);
    }
  };

  const onChange = (selectedDate: Date, selectedItem: string) => {
    const currentDate = selectedDate || autopayDate;
    let formattedDate = add(currentDate, { minutes: getDifferenceInTimeZone() });
    clearErrors(AutopayFields.PAYMENT_DATE);
    setAutopayDate(formattedDate);
    setAutopayDateDisplay(selectedItem);
    setValue(AutopayFields.PAYMENT_DATE, formattedDate);
  };

  const getPaymentDate = () => {
    const paymentDate = getValues(AutopayFields.PAYMENT_DATE);
    return paymentDate;
  };

  const getAmounts = () => {
    let paymentAmount = getValues("paymentAmount");
    if (!paymentAmount) return {};
    paymentAmount = Big(paymentAmount).mul(100);
    const amounts = {
      amount: Big(paymentAmount),
      // These are determined later
      platformFee: Big(0),
      total: Big(paymentAmount),
    };
    return amounts;
  };

  let paymentMethodsAutopayFlowAvailability = paymentMethodsAutopayFlow({
    shouldDisableCC: hasUserCCPayBlock,
    showOnlyCardAndBankMethod: true,
    ...params,
  });

  const fetchPropertyFees = async (amount: number, propertyId: string | number) => {
    await dispatch(getPropertyFees({ amount }, propertyId));
  };

  useEffect(() => {
    // get property fees
    const propertyId = params.currentProperty.id;
    if (propertyId) {
      fetchPropertyFees(getAmounts()?.amount, propertyId);
    }
  }, [amount]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={{ marginVertical: 20 }}>
        <PaymentAutopayFlowProgressIndicator currentPosition={0} />
      </View>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}>
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.cardTop]}>
                <View
                  style={styles.inputRowInner}
                  ref={(x) => Auryc.unMarkViewAsSensitiveInformation(x)}>
                  <AutopayFlowHeading
                    heading={translate("autopay.details.paymentDateTitle")}
                    automationTestingLocators={
                      automationTestingLocators.paymentAutopayDetails.paymentDateTitle
                    }
                  />
                  <DatePickerDropdownNewFixed
                    minimumDate={minimumDate}
                    maximumDate={maximumDate}
                    formName={AutopayFields.PAYMENT_DATE}
                    onChange={onChange}
                    title={translate("autopay.details.paymentDateCallToAction") as string}
                    {...automationTestingLocators.paymentAutopayDetails.paymentDatePicker}
                  />
                  <ErrorMessage errors={errors} formName={AutopayFields.PAYMENT_DATE} />
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ marginTop: 20 }} />
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <AutopayFlowHeading
                      heading={translate("autopay.details.paymentMethodsTitle")}
                      inputSectionHeaderStyle={styles.inputSectionHeader}
                      automationTestingLocators={
                        automationTestingLocators.paymentAutopayDetails.paymentMethodsTitle
                      }
                    />
                  </View>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          </Container>
        </Grid>
        <View {...automationTestingLocators.paymentAutopayDetails.paymentMethods}>
          <PaymentMethods
            paymentMethodsAvailabilityObject={paymentMethodsAutopayFlowAvailability}
            paymentSources={paymentSources}
            onPressItem={(item: any) => {
              updateSelected(item);
            }}
            selected={selected}
            preSelected={preSelected}
            finalAmounts={getAmounts()}
            paymentDate={getPaymentDate()}
            paymentSourcesError={paymentSourcesError}
            {...params}
          />
        </View>
      </ScrollView>
      <BottomNav backgroundColor={theme.colors.white}>
        <Text style={styles.ccFeeMessage}>{ccFeesMessage}</Text>
        <View style={{ marginTop: 5 }}></View>
        <Row>
          <Column span={{ xs: false, sm: 2, md: 3, lg: 3 }} />
          <Column span={{ xs: 12, sm: 8, md: 6, lg: 6 }}>
            <Button
              textStyle={{ fontFamily: "Lato_700Bold" }}
              disabled={getDisabled()}
              text={translate("autopay.details.next") as string}
              type={"primary"}
              onPress={handleSubmit(onSubmit)}
              {...automationTestingLocators.paymentAutopayDetails.paymentAutopayContinueButton}
            />
          </Column>
          <Column span={{ xs: false, sm: 2, md: 3, lg: 3 }} />
        </Row>
      </BottomNav>
      <Footer />
      <ErrorWarningBottomSheet
        ref={errorPopupRef}
        title={errorMsgObject?.title}
        body={errorMsgObject?.body}
        height={170}
        alertType={"error"}
        showButtons={false}
        {...automationTestingLocators.paymentAutopayDetails.errorAlert}
      />
      <VerifyBillingAddressNew
        ref={verifyBillAddressPopupRef}
        {...params}
        propertyUnit={propertyUnit}
        reservationId={reservationId}
        paymentDate={getPaymentDate()}
        amounts={finalAmountsState}
        paymentSourceId={selected?.id}
        paymentScheduleType={"RECURRING"}
        last4Digits={selected?.last_four_digits}
        paymentSourceType={selected?.type}
        cardType={selected?.card_type}
        isCertified={false}
        fromAutopay={true}
        nickname={selected?.nickname}
        selectedPaymentMethod={selected}
        toggleForCardBottomSheet={toggleForCardBottomSheet}
      />
    </SafeAreaView>
  );
};

const AutopayFlowHeading = ({
  heading,
  showTooltip = false,
  tooltipTitle,
  tooltipBody,
  inputSectionHeaderStyle,
  automationTestingLocators,
}: any) => {
  const { width: screenWidth } = useWindowDimensions();
  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <Text
          style={[
            typography.heading2,
            {
              fontFamily: "Lato_700Bold",
              fontSize: 14,
              color: theme.colors.text3,
            },
            inputSectionHeaderStyle,
          ]}
          {...automationTestingLocators}>
          {heading}
        </Text>
        {showTooltip && (
          <View style={{ marginLeft: 10 }}>
            <Tooltip
              width={"80%"}
              title={tooltipTitle}
              body={tooltipBody}
              widthWeb={screenWidth > 500 ? 350 : screenWidth * 0.7}>
              <Icon size={theme.iconSize.small} color={theme.colors.primary} name="error" />
            </Tooltip>
          </View>
        )}
      </View>
    </View>
  );
};
const getStyles = () => {
  return StyleSheet.create({
    container: {
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: "#FFFFFF",
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: "#FFFFFF",
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: "#FFFFFF",
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    scrollView: {
      flexGrow: 0,
    },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      }),
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    inputRowInner: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
      }),
    },
    cardTop: {
      paddingTop: 0,
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: "#FFFFFF" },
        webTablet: {
          paddingTop: 8,
          backgroundColor: "#FFFFFF",
          paddingLeft: 40,
          paddingRight: 40,
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingVertical: 8,
        },
        webDesktop: {
          paddingTop: 8,
          backgroundColor: "#FFFFFF",
          paddingLeft: 40,
          paddingRight: 40,
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingVertical: 8,
        },
        iosPhone: { backgroundColor: "#FFFFFF" },
        iosTablet: {
          paddingTop: 8,
          backgroundColor: "#FFFFFF",
          paddingLeft: 40,
          paddingRight: 40,
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingVertical: 8,
        },
        androidPhone: { backgroundColor: "#FFFFFF" },
        androidTablet: {
          paddingTop: 8,
          backgroundColor: "#FFFFFF",
          paddingLeft: 40,
          paddingRight: 40,
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
          paddingVertical: 8,
        },
      }),
    },
    inputSectionHeader: { fontFamily: "Lato_700Bold", fontSize: 14, color: theme.colors.text3 },
    ccFeeMessage: {
      textAlign: "center",
      fontFamily: "Lato_400Regular",
      fontSize: 12,
      color: theme.colors.text3,
    },
  });
};

export default PaymentAutoPayDetails;
